import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { Link } from 'react-router-dom';
import { Loader2, ShieldOff, ArrowLeft, Boxes, History } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import StockSummaryBar from '@/components/admin/stock/StockSummaryBar';
import StockInventoryTable from '@/components/admin/stock/StockInventoryTable';
import StockAdjustModal from '@/components/admin/stock/StockAdjustModal';
import StockReceiveModal from '@/components/admin/stock/StockReceiveModal';
import StockAuditLog from '@/components/admin/stock/StockAuditLog';
import {
  buildAllocationMap, buildStockRows, matchesFilter, matchesSearch,
  categoryKeyOf, matchesCategory,
} from '@/lib/stockControl';
import { useCategoryLabels } from '@/hooks/useCategoryLabels';

// Stock Control — live Square on-hand levels, trade-order commitments, reorder
// thresholds and manual adjustments with an audit trail. Catalogue setup
// (images, pricing, variations) stays in Admin → Products.
export default function AdminStockControl() {
  const { user, isLoadingAuth } = useAuth();
  const { toast } = useToast();
  const labelFor = useCategoryLabels();

  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [levels, setLevels] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncedAt, setSyncedAt] = useState('');
  const [tab, setTab] = useState('inventory');
  const [filter, setFilter] = useState('all');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [adjustRow, setAdjustRow] = useState(null);
  const [receiveRow, setReceiveRow] = useState(null);

  // Pull the live on-hand figure for every tracked variation from Square and
  // write any changed levels back onto the catalogue records.
  const refresh = useCallback(async (silent) => {
    setSyncing(true);
    try {
      const res = await base44.functions.invoke('stockControl', { action: 'refresh' });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Square sync failed');
      const nextLevels = res.data.levels || {};
      setLevels(nextLevels);
      setProducts((prev) => prev.map((p) => {
        const level = p.square_variation_id ? nextLevels[p.square_variation_id] : null;
        if (!level) return p;
        return { ...p, stock_quantity: level.quantity, in_stock: level.quantity > 0 };
      }));
      setSyncedAt(new Date(res.data.synced_at || Date.now()).toLocaleTimeString('en-GB'));
      if (!silent) {
        toast({
          title: 'Stock levels refreshed',
          description: `${res.data.tracked} tracked variation${res.data.tracked === 1 ? '' : 's'} read from Square`,
        });
      }
    } catch (e) {
      toast({ title: 'Square sync failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    }
    setSyncing(false);
  }, []);

  // Push the counted figures on this page up to Square as a physical count, then
  // read Square's confirmed levels straight back. Scoped to the category on
  // screen, so a stock take can be run and confirmed one category at a time.
  const reconcile = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await base44.functions.invoke('stockControl', {
        action: 'reconcile',
        category_keys: category ? [category] : [],
      });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Square sync failed');
      const data = res.data;
      const nextLevels = data.levels || {};
      setLevels(nextLevels);
      setProducts((prev) => prev.map((p) => {
        const level = p.square_variation_id ? nextLevels[p.square_variation_id] : null;
        if (!level) return p;
        return { ...p, stock_quantity: level.quantity, in_stock: level.quantity > 0 };
      }));
      setSyncedAt(new Date(data.synced_at || Date.now()).toLocaleTimeString('en-GB'));
      if (data.pushed > 0) {
        const adjs = await base44.entities.StockAdjustment.list('-created_date', 200);
        setAdjustments(adjs || []);
      }
      const parts = [`${data.pushed} pushed to Square`, `${data.already} already matching`];
      if (data.skipped) parts.push(`${data.skipped} not tracked`);
      if (data.failed) parts.push(`${data.failed} refused`);
      toast({
        title: data.failed ? 'Sync finished with refusals' : 'Stock synced with Square',
        description: parts.join(' · '),
        variant: data.failed ? 'destructive' : undefined,
      });
    } catch (e) {
      toast({ title: 'Square sync failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    }
    setSyncing(false);
  }, [category]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prods, ords, adjs] = await Promise.all([
        base44.entities.Product.list('-updated_date', 2000),
        base44.entities.B2BOrder.list('-created_date', 500),
        base44.entities.StockAdjustment.list('-created_date', 200),
      ]);
      setProducts(prods || []);
      setOrders(ords || []);
      setAdjustments(adjs || []);
      setLoading(false);
      await refresh(true);
    } catch (e) {
      setLoading(false);
      toast({ title: 'Failed to load stock data', description: e.message, variant: 'destructive' });
    }
  }, [refresh]);

  useEffect(() => { load(); }, [load]);

  const allocated = useMemo(() => buildAllocationMap(orders, products), [orders, products]);
  const rows = useMemo(() => buildStockRows(products, levels, allocated), [products, levels, allocated]);

  // Category options are derived from the rows actually loaded, so a category
  // only appears while it holds stock lines. Labels come from the Category
  // records, falling back to the Square reporting category name.
  const categoryCounts = new Map();
  for (const r of rows) {
    const key = categoryKeyOf(r.product);
    if (key) categoryCounts.set(key, (categoryCounts.get(key) || 0) + 1);
  }
  const categoryOptions = [...categoryCounts.entries()]
    .map(([key, count]) => ({
      key,
      count,
      label: key.startsWith('rc:') ? key.slice(3) : labelFor(key),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Narrowed to one category first, so the status-chip counts and the rows below
  // them always describe the same slice of stock.
  const categoryRows = useMemo(
    () => rows.filter((r) => matchesCategory(r, category)),
    [rows, category],
  );
  const visible = useMemo(
    () => categoryRows.filter((r) => matchesFilter(r, filter) && matchesSearch(r, search)),
    [categoryRows, filter, search],
  );

  const saveThreshold = async (product, value) => {
    try {
      await base44.entities.Product.update(product.id, { reorder_point: value });
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, reorder_point: value } : p)));
    } catch (e) {
      toast({ title: 'Could not save reorder point', description: e.message, variant: 'destructive' });
    }
  };

  const handleSaved = (data) => {
    const adj = data.adjustment;
    setLevels((prev) => ({
      ...prev,
      [adj.square_variation_id]: { state: data.state, quantity: data.quantity_after },
    }));
    setProducts((prev) => prev.map((p) => (
      p.id === adj.product_id ? { ...p, stock_quantity: data.quantity_after, in_stock: data.quantity_after > 0 } : p
    )));
    setAdjustments((prev) => [adj, ...prev]);
    setAdjustRow(null);
    toast({
      title: 'Stock adjusted in Square',
      description: `${adj.product_name}${adj.variation_name ? ` — ${adj.variation_name}` : ''} is now ${data.quantity_after}`,
    });
  };

  // A delivery booked in raises the Square level and the product's received
  // total, and the batch's unit cost becomes the product's cost price.
  const handleReceived = (data) => {
    const adj = data.adjustment;
    setLevels((prev) => ({
      ...prev,
      [adj.square_variation_id]: { state: data.state, quantity: data.quantity_after },
    }));
    setProducts((prev) => prev.map((p) => (
      p.id === adj.product_id
        ? {
            ...p,
            stock_quantity: data.quantity_after,
            in_stock: data.quantity_after > 0,
            stock_purchased: data.stock_purchased,
            cost_price_ex_vat: adj.unit_cost_ex_vat,
          }
        : p
    )));
    setAdjustments((prev) => [adj, ...prev]);
    setReceiveRow(null);
    toast({
      title: 'Stock received into Square',
      description: `${adj.product_name} is now ${data.quantity_after} · batch ${data.batch_reference}`,
    });
  };

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <Link to="/admin" className="inline-block mt-5 text-primary text-xs font-bold tracking-widest hover:underline">SIGN IN →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <header className="py-12 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-xs font-bold tracking-widest mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Boxes className="w-6 h-6 text-primary" />
            <p className="text-primary text-xs font-bold tracking-widest">INVENTORY</p>
          </div>
          <h1 className="text-white font-black text-2xl sm:text-4xl uppercase">Stock Control</h1>
          <p className="text-white/30 text-sm mt-1">
            Live Square stock levels, trade-order commitments and manual adjustments. Catalogue setup stays in Products.
          </p>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
        <div className="flex gap-2 mb-6 border-b border-white/10">
          {[
            { key: 'inventory', label: 'Inventory' },
            { key: 'audit', label: `Audit Log${adjustments.length ? ` (${adjustments.length})` : ''}` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-5 py-3 text-xs font-bold tracking-widest -mb-px border-b-2 transition-colors ${
                tab === t.key ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white/70'
              }`}
            >
              {t.key === 'audit' && <History className="w-3.5 h-3.5" />}
              {t.label.toUpperCase()}
            </button>
          ))}
        </div>

        {tab === 'inventory' ? (
          <div className="space-y-4">
            <StockSummaryBar
              rows={categoryRows}
              total={rows.length}
              categories={categoryOptions}
              category={category}
              setCategory={setCategory}
              filter={filter}
              setFilter={setFilter}
              search={search}
              setSearch={setSearch}
              onSync={reconcile}
              syncing={syncing}
              syncedAt={syncedAt}
            />
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : (
              <StockInventoryTable
                rows={visible}
                onAdjust={setAdjustRow}
                onReceive={setReceiveRow}
                onSaveThreshold={saveThreshold}
              />
            )}
            <p className="text-white/25 text-[11px]">
              Net Available = live Square on-hand minus stock already committed to trade orders that Square has not been adjusted for.
            </p>
          </div>
        ) : (
          <StockAuditLog adjustments={adjustments} loading={loading} />
        )}
      </section>

      <StockAdjustModal row={adjustRow} onClose={() => setAdjustRow(null)} onSaved={handleSaved} />
      <StockReceiveModal row={receiveRow} onClose={() => setReceiveRow(null)} onReceived={handleReceived} />
    </div>
  );
}